import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, GitBranch, Loader2, RefreshCw } from 'lucide-react';
import { flowSyncService } from '../../services/flowSyncService';

const WhatsAppFlowsPage = () => {
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchingId, setFetchingId] = useState(null);

  const loadDefinitions = useCallback(async (opts = { silent: false }) => {
    try {
      if (!opts.silent) {
        setLoading(true);
      }
      const data = await flowSyncService.listFlowDefinitions();
      setDefinitions(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || e.message || 'Failed to load flows');
      setDefinitions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDefinitions();
  }, [loadDefinitions]);

  const formatTime = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return String(iso);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const result = await flowSyncService.syncFlows();
      const listed = result?.listedCount ?? result?.syncedCount ?? 0;
      const skipped = result?.skippedCount ?? 0;
      const errors = result?.errors || [];
      let msg = `Listed ${listed} flow(s)`;
      if (skipped > 0) {
        msg += `, skipped ${skipped}`;
      }
      toast.success(msg);
      if (errors.length > 0) {
        toast.error(`${errors.length} error(s): ${errors.slice(0, 3).join('; ')}${errors.length > 3 ? '…' : ''}`);
      }
      await loadDefinitions({ silent: true });
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || e.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleRefreshList = async () => {
    try {
      setRefreshing(true);
      await loadDefinitions({ silent: true });
      toast.success('List refreshed');
    } finally {
      setRefreshing(false);
    }
  };

  const handleFetchJson = async (metaFlowId) => {
    try {
      setFetchingId(metaFlowId);
      await flowSyncService.fetchFlowJson(metaFlowId);
      toast.success('Flow JSON downloaded');
      await loadDefinitions({ silent: true });
    } catch (e) {
      console.error(e);
      const msg =
        e.response?.data && typeof e.response.data === 'string'
          ? e.response.data
          : e.response?.data?.message || e.message || 'Failed to fetch Flow JSON';
      toast.error(msg);
    } finally {
      setFetchingId(null);
    }
  };

  if (loading && definitions.length === 0) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-primary-100 flex items-center justify-center">
            <GitBranch className="h-6 w-6 text-primary-700" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">WhatsApp Flows</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Sync from WhatsApp refreshes the flow list from Meta. Use Update on each row to download Flow JSON for
              routing validation.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {syncing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <GitBranch className="h-4 w-4" />
            )}
            Sync from WhatsApp
          </button>
          <button
            type="button"
            onClick={handleRefreshList}
            disabled={refreshing || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh list
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Flow ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  List sync
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Flow JSON
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {definitions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500">
                    No flows yet. Click <strong>Sync from WhatsApp</strong> to load flows from Meta.
                  </td>
                </tr>
              ) : (
                definitions.map((row) => (
                  <tr key={row.flowDefinitionId || row.metaFlowId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{row.name || '—'}</td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-700">{row.metaFlowId || '—'}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium bg-green-100 text-green-800">
                        {row.status || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatTime(row.syncedAt)}</td>
                    <td className="px-4 py-3 text-sm">
                      {row.hasFlowJson ? (
                        <span className="text-green-700 font-medium">Synced</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700" title="Flow JSON not downloaded yet">
                          <AlertTriangle className="h-4 w-4 shrink-0" />
                          Missing
                        </span>
                      )}
                      {row.hasFlowJson && row.flowJsonSyncedAt && (
                        <div className="text-xs text-gray-500 mt-0.5">{formatTime(row.flowJsonSyncedAt)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleFetchJson(row.metaFlowId)}
                        disabled={fetchingId === row.metaFlowId}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {fetchingId === row.metaFlowId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : null}
                        Update
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppFlowsPage;
